var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Box, Popover, TextField, Typography } from '@mui/material';
import { RgbaColorPicker } from 'react-colorful';
import { PRESET_COLORS } from "../utils/color.util";
import chroma from 'chroma-js';
import { useTranslation } from "react-i18next";
var MEMORY_COLORS_KEY = 'crqlar-memory-colors';
var MAX_MEMORY_COLORS = 8;
var parseColor = function (color) {
    if (!color)
        return null;
    if (color === 'transparent')
        return { r: 0, g: 0, b: 0, a: 0 };
    try {
        var c = chroma(color);
        var _a = c.rgb(), r = _a[0], g = _a[1], b = _a[2];
        return { r: r, g: g, b: b, a: c.alpha() };
    }
    catch (_b) {
        return null;
    }
};
var rgbaToHex8 = function (_a) {
    var r = _a.r, g = _a.g, b = _a.b, a = _a.a;
    return chroma(r, g, b).alpha(a).hex('rgba').toUpperCase();
};
var normalizeHex = function (input) {
    try {
        var c = chroma(input);
        return c.hex('rgba').toUpperCase();
    }
    catch (_a) {
        return null;
    }
};
var isColorTransparent = function (color) {
    if (color === 'transparent')
        return true;
    try {
        return chroma(color).alpha() === 0;
    }
    catch (_a) {
        return false;
    }
};
var CMemoryColorPicker = function (_a) {
    var color = _a.color, onChange = _a.onChange, _b = _a.disabled, disabled = _b === void 0 ? false : _b;
    var t = useTranslation().t;
    var _c = useState(null), anchorEl = _c[0], setAnchorEl = _c[1];
    var _d = useState({
        r: 0,
        g: 0,
        b: 0,
        a: 1
    }), internalColor = _d[0], setInternalColor = _d[1];
    var _e = useState(''), inputValue = _e[0], setInputValue = _e[1];
    var _f = useState([]), memoryColors = _f[0], setMemoryColors = _f[1];
    var isInternalChange = useRef(false);
    useEffect(function () {
        try {
            var stored = localStorage.getItem(MEMORY_COLORS_KEY);
            if (stored)
                setMemoryColors(JSON.parse(stored));
        }
        catch (_a) {
        }
    }, []);
    // Sync internal state only when the color prop changes externally
    useEffect(function () {
        if (isInternalChange.current) {
            isInternalChange.current = false;
            return;
        }
        var parsed = parseColor(color);
        if (parsed) {
            setInternalColor(parsed);
            setInputValue(rgbaToHex8(parsed));
        }
        else if (color) {
            setInputValue(color);
        }
    }, [color]);
    var emitChange = function (hex) {
        isInternalChange.current = true;
        onChange(hex);
    };
    var saveToMemory = function (newColor) {
        if (!newColor || isColorTransparent(newColor))
            return;
        var normalized = normalizeHex(newColor);
        if (!normalized)
            return;
        setMemoryColors(function (prev) {
            var updated = __spreadArray([normalized], prev.filter(function (c) { return c.toUpperCase() !== normalized; }), true).slice(0, MAX_MEMORY_COLORS);
            localStorage.setItem(MEMORY_COLORS_KEY, JSON.stringify(updated));
            return updated;
        });
    };
    var handleClose = function () {
        setAnchorEl(null);
        var parsed = parseColor(inputValue);
        if (parsed) {
            var normalized = rgbaToHex8(parsed);
            setInputValue(normalized);
            saveToMemory(normalized);
        }
        else {
            // Revert to the current valid color if input is invalid
            var fallback = parseColor(color);
            if (fallback) {
                var hex = rgbaToHex8(fallback);
                setInternalColor(fallback);
                setInputValue(hex);
            }
        }
    };
    var handleColorChange = function (newColor) {
        setInternalColor(newColor);
        var hex = rgbaToHex8(newColor);
        setInputValue(hex);
        emitChange(hex);
    };
    var handleInputChange = function (e) {
        var val = e.target.value;
        setInputValue(val);
        var parsed = parseColor(val);
        if (parsed) {
            setInternalColor(parsed);
            emitChange(rgbaToHex8(parsed));
        }
    };
    var isInputValid = !inputValue || parseColor(inputValue) !== null;
    var handleInputBlur = function () {
        var parsed = parseColor(inputValue);
        if (parsed) {
            var normalized = rgbaToHex8(parsed);
            setInputValue(normalized);
            emitChange(normalized);
        }
    };
    var handlePresetClick = function (preset) {
        if (preset === 'transparent') {
            var transparent = { r: 0, g: 0, b: 0, a: 0 };
            setInternalColor(transparent);
            setInputValue('#00000000');
            emitChange('#00000000');
        }
        else {
            var parsed = parseColor(preset);
            if (parsed) {
                setInternalColor(parsed);
                var hex = rgbaToHex8(parsed);
                setInputValue(hex);
                emitChange(hex);
            }
        }
    };
    var isTransparent = isColorTransparent(color);
    var displayColor = isTransparent ? 'transparent' : color;
    var checkerboard8 = {
        backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
        backgroundSize: '8px 8px',
        backgroundPosition: '0 0, 0 4px, 4px 0, 0 4px',
    };
    var checkerboard6 = {
        backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
        backgroundSize: '6px 6px',
        backgroundPosition: '0 0, 0 3px, 3px 0, 0 3px',
    };
    return (_jsxs(_Fragment, { children: [_jsx(Box, { onClick: function (e) { return !disabled && setAnchorEl(e.currentTarget); }, sx: __assign({ width: 32, height: 32, borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, bgcolor: displayColor }, (isTransparent ? checkerboard8 : {})) }), _jsxs(Popover, __assign({ open: Boolean(anchorEl), anchorEl: anchorEl, onClose: handleClose, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, slotProps: { paper: { sx: { p: 1.5, width: 220 } } } }, { children: [_jsx(RgbaColorPicker, { color: internalColor, onChange: handleColorChange, style: { width: '100%', height: 140 } }), _jsx(TextField, { fullWidth: true, size: "small", value: inputValue, onChange: handleInputChange, onBlur: handleInputBlur, placeholder: "#RGB, #RRGGBB, #RRGGBBAA", error: !isInputValid, sx: {
                            mt: 1,
                            '& input': {
                                fontSize: '0.75rem',
                                py: 0.5,
                                fontFamily: 'monospace'
                            }
                        } }), _jsx(Typography, __assign({ variant: "caption", color: "text.secondary", sx: { mt: 1, display: 'block' } }, { children: t('Presets') })), _jsx(Box, __assign({ sx: { display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 } }, { children: PRESET_COLORS.map(function (preset) { return (_jsx(Box, { onClick: function () { return handlePresetClick(preset); }, sx: __assign(__assign({ width: 20, height: 20, borderRadius: 0.5, border: '1px solid', borderColor: 'divider', cursor: 'pointer', bgcolor: preset === 'transparent' ? 'transparent' : preset }, (preset === 'transparent' ? checkerboard6 : {})), { '&:hover': { borderColor: 'primary.main' } }) }, preset)); }) })), memoryColors.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, __assign({ variant: "caption", color: "text.secondary", sx: { mt: 1, display: 'block' } }, { children: t('Recent') })), _jsx(Box, __assign({ sx: {
                                    display: 'flex',
                                    gap: 0.5,
                                    flexWrap: 'wrap',
                                    mt: 0.5
                                } }, { children: memoryColors.map(function (mem, i) { return (_jsx(Box, { onClick: function () { return handlePresetClick(mem); }, sx: {
                                        width: 20,
                                        height: 20,
                                        borderRadius: 0.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        cursor: 'pointer',
                                        bgcolor: mem,
                                        '&:hover': { borderColor: 'primary.main' },
                                    } }, "".concat(mem, "-").concat(i))); }) }))] }))] }))] }));
};
export default CMemoryColorPicker;
